import { saveAs } from 'file-saver';
import { dump } from 'js-yaml';
import { curry } from 'lodash-es';
import { HttpError } from '~/k8s/error';
import { ApplicationKind } from '~/types';
import { PipelineRunModel } from '../models';
import { K8sModelCommon, K8sResourceCommon, OwnerReference } from '../types/k8s';

export const getApplicationDisplayName = (application: ApplicationKind): string =>
  application?.spec?.displayName ?? application?.metadata?.name;

export const getResourceFromOwnerReference = curry(
  (model: K8sModelCommon, resource: K8sResourceCommon): OwnerReference => {
    return resource
      ? resource.metadata.ownerReferences?.find((res) => res.kind === model.kind)
      : undefined;
  },
);

export const getPipelineRunFromTaskRunOwnerRef = getResourceFromOwnerReference(PipelineRunModel);

export const has404Error = (error: unknown): error is HttpError => {
  return error instanceof HttpError && error.code === 404;
};

export const downloadYaml = <T extends K8sResourceCommon>(resource: T) => {
  if (!resource) return;
  const resourceYaml = dump(resource) as string;
  const blob = new Blob([resourceYaml], {
    type: 'text/yaml;charset=utf-8',
  });
  const filename = `${resource.metadata?.name || 'resource'}.yaml`;
  saveAs(blob, filename);
};

/** Returns a Download YAML action (action-menu Action shape). Use createDetailsPageAction() for DetailsPage. */
export const downloadYamlAction = <T extends K8sResourceCommon>(obj: T) => {
  const id = `download-${(obj.kind ?? 'resource').toLowerCase()}-yaml`;
  return {
    id,
    label: 'Download YAML',
    cta: () => downloadYaml(obj),
  };
};

/**
 * Parse a string value to boolean
 * @param value - String value ("true"/"false" or undefined)
 * @param defaultValue - Default value if undefined
 */
export function parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined || value === '') {
    return defaultValue;
  }
  return value.toLowerCase() === 'true';
}

/**
 * Parse a string value to number
 * @param value - String value or undefined
 * @param defaultValue - Default value if undefined or invalid
 */
export function parseNumber(value: string | undefined, defaultValue: number): number {
  if (value === undefined || value === '') {
    return defaultValue;
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

type TimeValue = Date | number | string;

const timeValueInMilliseconds = (value: TimeValue): number => {
  if (value instanceof Date) {
    return value.getTime();
  }

  return typeof value === 'number' ? value : Date.parse(value);
};

/** Returns a new array sorted by the selected time value in ascending order. */
export const sortByTime = <T,>(items: readonly T[], getTime: (item: T) => TimeValue): T[] => {
  const sortFunction = (a: T, b: T) =>
    timeValueInMilliseconds(getTime(a)) - timeValueInMilliseconds(getTime(b));

  return [...items].sort(sortFunction);
};
